/**
 * gameLines — best Moneyline / Spread / Total for one fixture, for the game
 * ticker's hover card.
 *
 * Cache-only by construction: the only network call is cacheService's
 * getCachedOdds (POST /getCachedOdds), which reads Firestore odds_markets via
 * functions/cacheHelper.checkCache and never calls OpticOdds. It is the same
 * cache step the search runs before nlOdds, and dataFetch routes it to the
 * frozen snapshot when an admin has Frozen data on.
 *
 * Most cards paint from precomputed odds_cache topLines (precomputedLines);
 * otherwise fetched when a card opens fresh (prefetchGameLines), kept per fixture
 * for 2 minutes, and concurrent opens of the same fixture share one request. Odds are a sandbox
 * feed right now (+100 everywhere), so this reports prices and books only,
 * never EV.
 */
import { getCachedOdds } from './cacheService';
import { canonicalBookName } from '../utils/cardBooks';
import { isLayBookName } from '../utils/canonicalEv';
import { getStoredDataSource } from '../utils/dataSource';

export const GAME_LINES_TTL_MS = 2 * 60 * 1000;

const cache = new Map();    // "source:fixtureId" -> { at, value }
const inflight = new Map(); // "source:fixtureId" -> Promise

/** Test hook. */
export function _resetGameLinesCache() {
  cache.clear();
  inflight.clear();
}

/**
 * Main-market names the odds refresh writes, per sport (functions/oddsRefresh
 * getMarketsForSport, functions/MARKET_SPORT_MAPPING.json). A list is tried in
 * order until one has cached odds (hockey's Puck Line is a derivative market
 * that isn't always populated).
 */
const SPORT_MARKETS = {
  basketball: { spread: ['Point Spread'], total: ['Total Points'] },
  football: { spread: ['Point Spread'], total: ['Total Points'] },
  hockey: { spread: ['Puck Line', 'Goal Spread'], total: ['Total Goals'] },
  baseball: { spread: ['Run Line'], total: ['Total Runs'] },
  soccer: { spread: ['Asian Handicap'], total: ['Total Goals'] },
  tennis: { spread: ['Game Spread'], total: ['Total Games'] },
  esports: { spread: ['Map Handicap'], total: ['Total Maps'] },
  mma: { spread: [], total: ['Total Rounds'] },
  boxing: { spread: [], total: ['Total Rounds'] },
  cricket: { spread: [], total: ['Total Runs'] },
};

export function sportOf(fixture) {
  const s = String(fixture?.sport || '').toLowerCase();
  if (SPORT_MARKETS[s]) return s;
  const l = String(fixture?.league || '').toLowerCase();
  if (/nba|ncaab|wnba|euroleague|basketball/.test(l)) return 'basketball';
  if (/nfl|ncaaf|cfl|ufl/.test(l)) return 'football';
  if (/nhl|hockey|khl|ahl/.test(l)) return 'hockey';
  if (/mlb|kbo|npb|baseball/.test(l)) return 'baseball';
  if (/atp|wta|tennis/.test(l)) return 'tennis';
  if (/ufc|mma|bellator|pfl/.test(l)) return 'mma';
  if (/boxing/.test(l)) return 'boxing';
  if (/mls|epl|premier|liga|bundesliga|serie|ligue|uefa|champions|soccer/.test(l)) return 'soccer';
  return 'basketball';
}

/** { moneyline, spread, total }: market names to try, in order, for this fixture ([] = the sport has none). */
export function marketsFor(fixture) {
  const m = SPORT_MARKETS[sportOf(fixture)] || SPORT_MARKETS.basketball;
  return { moneyline: ['Moneyline'], spread: m.spread, total: m.total };
}

// ── Picking ────────────────────────────────────────────────────────────────

const toNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/^\+/, ''));
  return Number.isFinite(n) ? n : null;
};
/** American price → decimal payout, to compare which price pays more. */
const payout = (american) => (american >= 0 ? 1 + american / 100 : 1 + 100 / Math.abs(american));
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * 'away' | 'home' | null for a selection. Full names win; the nickname is a
 * fallback only when it tells the teams apart ("Sox" can't split Red Sox vs White Sox).
 */
function sideClassifier(awayTeam, homeTeam) {
  const parts = (team) => {
    const full = norm(team);
    const words = full.split(' ').filter(Boolean);
    const nick = words[words.length - 1] || '';
    return { full, nickRe: nick.length > 2 ? new RegExp(`\\b${nick}\\b`) : null };
  };
  const a = parts(awayTeam);
  const h = parts(homeTeam);
  return (sel) => {
    const s = norm(sel);
    if (!s) return null;
    const af = !!a.full && s.includes(a.full);
    const hf = !!h.full && s.includes(h.full);
    if (af !== hf) return af ? 'away' : 'home';
    if (af && hf) return null;
    const an = !!a.nickRe && a.nickRe.test(s);
    const hn = !!h.nickRe && h.nickRe.test(s);
    if (an !== hn) return an ? 'away' : 'home';
    return null;
  };
}

/** Signed line: the odd's own line field, else the trailing number in the selection text. */
function lineOf(o) {
  const own = toNum(o.selection_line ?? o.line ?? o.points ?? o.handicap);
  if (own !== null) return own;
  const m = /([+-]?\d+(?:\.\d+)?)\s*$/.exec(String(o.selection || o.name || ''));
  return m ? Number(m[1]) : null;
}

function bestOf(rows) {
  let best = null;
  for (const r of rows) {
    if (!best) { best = r; continue; }
    const a = payout(r.price);
    const b = payout(best.price);
    // Ties (every price on the sandbox feed) resolve alphabetically so the card is stable.
    if (a > b + 1e-9 || (Math.abs(a - b) <= 1e-9 && r.book.localeCompare(best.book) < 0)) best = r;
  }
  return best ? { price: best.price, book: best.book, line: best.line } : null;
}

/** Rows on the main line: is_main-flagged rows first, else the number most rows quote. */
function mainLineRows(rows, keyFn) {
  const flagged = rows.filter((r) => r.isMain);
  const pool = flagged.length ? flagged : rows;
  const counts = new Map();
  for (const r of pool) counts.set(keyFn(r), (counts.get(keyFn(r)) || 0) + 1);
  let pick = null;
  let max = 0;
  for (const [k, c] of counts) if (c > max) { pick = k; max = c; }
  return pick === null ? [] : pool.filter((r) => keyFn(r) === pick);
}

function usable(o, fixtureId) {
  if (!o || typeof o !== 'object') return null;
  // The cache read returns every game the team has; keep only rows tagged with this one.
  if (fixtureId && String(o.fixture_id ?? '') !== String(fixtureId)) return null;
  const price = toNum(o.price ?? o.american_odds ?? o.odds);
  const rawBook = o.sportsbook || o.book;
  if (price === null || price === 0 || !rawBook || isLayBookName(rawBook)) return null;
  return {
    price,
    book: canonicalBookName(rawBook) || String(rawBook),
    sel: String(o.selection || o.name || ''),
    line: lineOf(o),
    isMain: o.is_main === true,
  };
}

/**
 * Best price per side for the three main markets:
 *   { moneyline: { away, home }, spread: { away, home }, total: { over, under } }
 * Each side is { price, book, line } or null when the cache has nothing for it.
 */
export function pickBestLines(oddsByMarket, fixture) {
  const fixtureId = fixture?.fixture_id || fixture?.id;
  const sideOf = sideClassifier(fixture?.away_team, fixture?.home_team);
  const rowsFor = (key) => (Array.isArray(oddsByMarket?.[key]) ? oddsByMarket[key] : [])
    .map((o) => usable(o, fixtureId)).filter(Boolean)
    .map((r) => ({ ...r, side: sideOf(r.sel) }));

  const ml = rowsFor('moneyline').filter((r) => !/\b(draw|tie)\b/i.test(r.sel));
  const moneyline = {
    away: bestOf(ml.filter((r) => r.side === 'away')),
    home: bestOf(ml.filter((r) => r.side === 'home')),
  };

  // Spread: one side's main line, then the other side on the mirrored number
  // (away +3.5 pairs with home -3.5, never an alt away -3.5).
  const sp = rowsFor('spread').filter((r) => r.line !== null && r.side);
  const spHomeAll = sp.filter((r) => r.side === 'home');
  const spAwayAll = sp.filter((r) => r.side === 'away');
  const anchor = spHomeAll.length ? spHomeAll : spAwayAll;
  const anchorMain = mainLineRows(anchor, (r) => r.line);
  const anchorLine = anchorMain.length ? anchorMain[0].line : null;
  const pairRows = anchorLine === null ? [] : (anchor === spHomeAll ? spAwayAll : spHomeAll).filter((r) => r.line === -anchorLine);
  const spread = anchor === spHomeAll
    ? { home: bestOf(anchorMain), away: bestOf(pairRows) }
    : { away: bestOf(anchorMain), home: bestOf(pairRows) };

  const tot = rowsFor('total').filter((r) => r.line !== null && /\b(over|under)\b/i.test(r.sel));
  const totMain = mainLineRows(tot, (r) => Math.abs(r.line));
  const over = bestOf(totMain.filter((r) => /\bover\b/i.test(r.sel)));
  const under = bestOf(totMain.filter((r) => /\bunder\b/i.test(r.sel)));
  const total = {
    over: over ? { ...over, line: Math.abs(over.line) } : null,
    under: under ? { ...under, line: Math.abs(under.line) } : null,
  };

  return { moneyline, spread, total };
}

// ── Precomputed (fixture sweep) ────────────────────────────────────────────

const MIN_MS = 60 * 1000;
const HOUR_MS = 60 * MIN_MS;
/**
 * How old a precomputed value may be, by hours to kick-off. Two writers refresh
 * topLinesAt: the NFL / NCAAF fixture sweep (every 12 min ≤ 6 h out, 55 min ≤ 48 h,
 * 6 h ≤ 7 d, else 24 h; functions/utils/sweepPlan.js BANDS) and, for every other
 * ticker game and live games, scheduledTickerTopLines (every 10 min ≤ 6 h out or
 * live, hourly ≤ 48 h, ~5 h ≤ 7 d, ~20 h beyond; functions/tickerTopLines.js
 * DUE_BANDS). These allow about two missed visits before the card reads lazily.
 */
const PRECOMPUTED_MAX_AGE = [
  { withinHours: 6, maxAgeMs: 30 * MIN_MS },
  { withinHours: 48, maxAgeMs: 2 * HOUR_MS },
  { withinHours: 7 * 24, maxAgeMs: 13 * HOUR_MS },
  { withinHours: Infinity, maxAgeMs: 49 * HOUR_MS },
];

function startMs(fixture) {
  if (Number.isFinite(fixture?.start_time_ms)) return fixture.start_time_ms;
  const d = fixture?.start_date;
  if (d && typeof d === 'object') {
    const secs = d._seconds ?? d.seconds;
    if (Number.isFinite(secs)) return secs * 1000;
  }
  const t = d ? Date.parse(d) : NaN;
  return Number.isFinite(t) ? t : null;
}

const isLinesShape = (v) => !!v && typeof v === 'object'
  && ['moneyline', 'spread', 'total'].every((k) => v[k] && typeof v[k] === 'object');

/**
 * The stored lines (odds_cache topLines, from the fixture sweep or
 * scheduledTickerTopLines) when still current for this game, else null and the
 * card reads lazily. Started games use the tightest window.
 */
export function precomputedLines(fixture, { now = Date.now } = {}) {
  // Precomputed lines are live data; Frozen (admin) cards read the snapshot lazily.
  if (getStoredDataSource() === 'frozen') return null;
  const v = fixture?.topLines;
  const at = fixture?.topLinesAt;
  if (!isLinesShape(v) || !Number.isFinite(at)) return null;
  const t = now();
  const start = startMs(fixture);
  const hoursOut = start === null ? 0 : Math.max(0, (start - t) / HOUR_MS);
  const { maxAgeMs } = PRECOMPUTED_MAX_AGE.find((b) => hoursOut <= b.withinHours);
  const age = t - at;
  return age >= 0 && age < maxAgeMs ? v : null;
}

// ── Loading ────────────────────────────────────────────────────────────────

const fixtureIdOf = (fixture) => String(fixture?.fixture_id || fixture?.id || '');
/** Live and Frozen (admin) data never share an entry. */
const cacheKey = (id) => `${getStoredDataSource()}:${id}`;

/** First market name (in order) with cached odds for this fixture. A failed read throws, so it isn't cached. */
async function fetchMarket(fixture, names) {
  const id = fixtureIdOf(fixture);
  for (const market of names || []) {
    const res = await getCachedOdds({
      league: fixture.league || undefined,
      sport: fixture.sport || undefined,
      team: fixture.home_team,
      market,
    });
    if (!res || res.success === false) throw new Error(res?.message || 'Cached odds read failed');
    const odds = Array.isArray(res.odds) ? res.odds.filter((o) => String(o?.fixture_id ?? '') === id) : [];
    if (odds.length) return odds;
  }
  return [];
}

/** Best lines for a fixture, cached GAME_LINES_TTL_MS per fixture; concurrent calls share one request. */
export function loadGameLines(fixture, { now = Date.now } = {}) {
  const id = fixtureIdOf(fixture);
  if (!id || !fixture?.home_team) return Promise.resolve(pickBestLines({}, fixture));
  const key = cacheKey(id);
  const hit = cache.get(key);
  if (hit && now() - hit.at < GAME_LINES_TTL_MS) return Promise.resolve(hit.value);
  if (inflight.has(key)) return inflight.get(key);

  const names = marketsFor(fixture);
  const p = Promise.all([
    fetchMarket(fixture, names.moneyline),
    fetchMarket(fixture, names.spread),
    fetchMarket(fixture, names.total),
  ])
    .then(([moneyline, spread, total]) => {
      const value = pickBestLines({ moneyline, spread, total }, fixture);
      cache.set(key, { at: now(), value });
      return value;
    })
    .finally(() => { inflight.delete(key); });
  inflight.set(key, p);
  return p;
}

/** A fresh cached entry, read synchronously (a reopened card paints without a loading flash). */
export function peekGameLines(fixture, { now = Date.now } = {}) {
  const id = fixtureIdOf(fixture);
  const hit = id ? cache.get(cacheKey(id)) : null;
  return hit && now() - hit.at < GAME_LINES_TTL_MS ? hit.value : null;
}

/** True while a read for this fixture is in flight (a card opening now joins it at once). */
export function isLoadingGameLines(fixture) {
  const id = fixtureIdOf(fixture);
  return !!id && inflight.has(cacheKey(id));
}

/**
 * Start the lazy read early (fire and forget) unless the card can already paint:
 * fresh precomputed lines or a fresh cached entry. Errors are left for the card's
 * own load, which retries (a failed read isn't cached).
 */
export function prefetchGameLines(fixture, { now = Date.now } = {}) {
  if (!fixtureIdOf(fixture) || !fixture?.home_team) return;
  if (precomputedLines(fixture, { now }) || peekGameLines(fixture, { now })) return;
  loadGameLines(fixture, { now }).catch(() => {});
}
